import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Badge from '../../components/ui/Badge';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  TableIcon,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { ChartTrendingUpIcon } from '../../components/ui/Icons';
import { formatDate } from '../../config/appConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import type { ListQueryOptions } from '../../services/paginatedList';
import { getPublishedVersion, usePublishedEForms } from './useData';

type EnabledTab = 'active' | 'inactive' | 'all';

const enabledTabs: Array<{ id: EnabledTab; label: string; icon: React.ReactNode }> = [
  { id: 'active', label: 'Enabled', icon: <CheckCircleIcon className="h-4 w-4" /> },
  { id: 'inactive', label: 'Disabled', icon: <FolderIcon className="h-4 w-4" /> },
  { id: 'all', label: 'All', icon: <DocumentTextIcon className="h-4 w-4" /> },
];

/** Catalog of published forms → open the pivot report builder for each. */
const ReportBuilderList: React.FC = () => {
  const { can } = useAuth();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const canManage = can('e_form_definitions', 'edit');
  const canViewReports = can('e_form_submissions', 'view') || can('e_form_submissions', 'edit');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<EnabledTab>('active');
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'name', direction: 'asc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'createdAt' || key === 'updatedAt' ? 'desc' : 'asc',
    },
  );

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, tab, itemsPerPage, sortConfig.key, sortConfig.direction, canManage]);

  const listOptions = useMemo((): ListQueryOptions => {
    const filters: Record<string, string[]> = {};
    if (canManage) {
      if (tab === 'active') filters.isActive = ['Yes'];
      if (tab === 'inactive') filters.isActive = ['No'];
    } else {
      filters.isActive = ['Yes'];
    }
    return {
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      filters: Object.keys(filters).length ? { filters } : undefined,
    };
  }, [canManage, tab, sortConfig.key, sortConfig.direction]);

  const { data, total, isLoading } = usePublishedEForms(
    search || undefined,
    page,
    itemsPerPage,
    listOptions,
  );

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / itemsPerPage));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasFilters = Boolean(search.trim() || (canManage && tab !== 'all'));
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    if (canManage) setTab('all');
  };

  if (!canViewReports) {
    return (
      <div className={cn('p-6 text-sm', colors.textMuted)}>
        You do not have permission to view e-form reports.
      </div>
    );
  }

  return (
    <GeneralList
      title="Report builder"
      tabs={canManage ? enabledTabs.map(({ id, label, icon }) => ({ id, label, icon })) : undefined}
      activeTab={canManage ? tab : undefined}
      onTabChange={canManage ? setTab : undefined}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search forms by name…',
      }}
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading forms…"
      isEmpty={!isLoading && data.length === 0}
      emptyState={{
        icon: <ChartTrendingUpIcon className="h-12 w-12" />,
        title: hasFilters ? 'No forms match your search' : 'No published forms yet',
        hint: hasFilters
          ? 'Try adjusting filters or clearing the search.'
          : 'Publish a form in Form Studio to create reports.',
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : (
          <Link
            to="/e-forms/builder"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            Open Form Studio
          </Link>
        ),
      }}
      pagination={{
        page,
        totalPages,
        total: total ?? 0,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className={cn('sticky top-0 z-10 w-px whitespace-nowrap font-medium', colors.surface)} />
            <GeneralListSortableTh sortKey="name" className="whitespace-nowrap">
              Name
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 whitespace-nowrap font-medium', colors.surface, colors.textMuted)}>
              Published
            </th>
            <GeneralListSortableTh sortKey="isActive" className="whitespace-nowrap">
              Enabled
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 whitespace-nowrap font-medium', colors.surface, colors.textMuted)}>
              Records
            </th>
            <th className={cn('sticky top-0 z-10 font-medium', colors.surface, colors.textMuted)}>
              Description
            </th>
            <GeneralListSortableTh sortKey="updatedAt" className="whitespace-nowrap">
              Updated
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 w-full font-medium', colors.surface)} aria-hidden />
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const pub = getPublishedVersion(row);
            const reportsPath = canManage
              ? `/e-forms/builder/${row.id}?section=reports`
              : `/e-forms/${row.id}/reports`;
            return (
              <tr
                key={row.id}
                className={cn(
                  'cursor-pointer border-b transition-colors last:border-b-0',
                  colors.border,
                  colors.text,
                  colors.interactiveHover,
                )}
                onClick={() => navigate(reportsPath)}
              >
                <td className="w-px whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <div className="flex flex-nowrap items-center gap-0.5">
                    <Link
                      to={reportsPath}
                      className={cn(
                        'rounded-lg p-1 transition-colors',
                        colors.primaryFg,
                        colors.interactiveHover,
                      )}
                      title="Open report builder"
                      aria-label="Open report builder"
                    >
                      <ChartTrendingUpIcon className="h-5 w-5" />
                    </Link>
                    <Link
                      to={`/e-forms/${row.id}/submissions`}
                      className={cn(
                        'rounded-lg p-1 transition-colors',
                        colors.textMuted,
                        colors.interactiveHover,
                      )}
                      title="Submissions"
                      aria-label="Submissions"
                    >
                      <TableIcon className="h-5 w-5" />
                    </Link>
                  </div>
                </td>
                <td className="whitespace-nowrap font-medium">{row.name}</td>
                <td className="whitespace-nowrap">
                  {pub ? (
                    <Badge variant="success">v{pub.version}</Badge>
                  ) : (
                    <Badge variant="secondary">Draft only</Badge>
                  )}
                </td>
                <td className="whitespace-nowrap">
                  <Badge variant={row.isActive ? 'success' : 'danger'}>
                    {row.isActive ? 'Yes' : 'No'}
                  </Badge>
                </td>
                <td className="whitespace-nowrap tabular-nums">{row._count?.submissions ?? 0}</td>
                <td className="max-w-[18rem]">
                  <span
                    className={cn('block truncate', colors.textMuted)}
                    title={row.description ?? undefined}
                  >
                    {row.description?.trim() ? row.description : '—'}
                  </span>
                </td>
                <td className="whitespace-nowrap">
                  {row.updatedAt ? formatDate(String(row.updatedAt)) : '—'}
                </td>
                <td aria-hidden />
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default ReportBuilderList;
